import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Syncs active tab state with the `?tab=` URL search parameter.
 * Falls back to `defaultTab` when no param is present.
 */
export default function useTabFromUrl(defaultTab) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [tab, setTabState] = useState(urlTab || defaultTab);

  const setTab = useCallback((next) => {
    setTabState(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === defaultTab) {
        p.delete('tab');
      } else {
        p.set('tab', next);
      }
      return p;
    }, { replace: true });
  }, [defaultTab, setSearchParams]);

  return [tab, setTab];
}

/**
 * Syncs a sub-tab with `?subtab=` URL param, scoped to a parent tab value.
 * When the parent tab is not active, the subtab param is cleaned up.
 *
 * @param {string} parentParamValue - the parent tab value that activates this sub-tab (e.g. 'admin')
 * @param {string} defaultSubTab - fallback sub-tab when no param is present
 */
export function useSubTabFromUrl(parentParamValue, defaultSubTab) {
  const [searchParams, setSearchParams] = useSearchParams();
  const parentTab = searchParams.get('tab');
  const urlSubTab = searchParams.get('subtab');
  const isParentActive = parentTab === parentParamValue;

  const [subTab, setSubTabState] = useState(
    isParentActive && urlSubTab ? urlSubTab : defaultSubTab,
  );

  const setSubTab = useCallback((next) => {
    setSubTabState(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === defaultSubTab) {
        p.delete('subtab');
      } else {
        p.set('subtab', next);
      }
      return p;
    }, { replace: true });
  }, [defaultSubTab, setSearchParams]);

  // Clean up subtab param when parent tab changes away
  const effectiveSubTab = isParentActive ? subTab : defaultSubTab;

  return [effectiveSubTab, setSubTab];
}
